import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import TableMap from './TableMap';
import TableSummary from './TableSummary';
import { getTables, updateTable } from '../../../../api/tableAPI';
import { getTodayBookings, updateFullBooking } from '../../../../api/bookingApi';
import { TABLE_MAP } from '../../../../constants/tabledata';
import './css/TableManagement.css';

export default function TableManagement() {
  const { t } = useTranslation();
  const [tables, setTables] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch tables and bookings in parallel
        const [tablesResponse, todayBookingsResponse] = await Promise.all([
          getTables({}),
          getTodayBookings({page: 0, size: 100})
        ]);

        // Process tables data
        if (tablesResponse && Array.isArray(tablesResponse)) {
          const table_map = tablesResponse
            .filter(table => TABLE_MAP.some(m => m.id === table.tableId))
            .map(table => {
              const mapData = TABLE_MAP.find(m => m.id === table.tableId);
              return {
                ...table,
                position: mapData.position
              };
            });
          setTables(table_map);
        } else {
          console.warn('Tables response is not an array:', tablesResponse);
          setTables([]);
        }

        // Process bookings data - Fixed the bug here
        if (todayBookingsResponse && Array.isArray(todayBookingsResponse.content)) {
          setBookings(todayBookingsResponse.content);
        } else if (todayBookingsResponse && Array.isArray(todayBookingsResponse)) {
          setBookings(todayBookingsResponse);
        } else {
          console.warn('Bookings response format unexpected:', todayBookingsResponse);
          setBookings([]);
        }

      } catch (err) {
        console.error('Error loading table data:', err);
        setError(t('admin.table_management.errors.load_failed', { message: err.message }));
        setTables([]);
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [t]);

  const handleUpdateTableStatus = async (table, newStatus) => {
    try {
      const body = { ...table };
      body.status = newStatus;
      
      const response = await updateTable(table.tableId, body);
      console.log(response);
      
      // Update the tables state with the new status
      if (response && response.status === newStatus) {
        setTables(prevTables =>
          prevTables.map(t =>
            t.tableId === table.tableId
              ? { ...t, status: newStatus }
              : t
          )
        );
      } else {
        console.warn('Table status not updated on server or mismatch:', response);
        setError(t('admin.table_management.errors.update_failed'));
      }
    } catch (error) {
      console.error('Error updating table status:', error);
      setError(t('admin.table_management.errors.table_status_update_failed', { message: error.message }));
    }
  };

  const handleUpdateBookingStatus = async (booking, newStatus) => {
    try {
      const body = { ...booking };
      body.status = newStatus;
      
      const response = await updateFullBooking(booking.bookingId, body);
      
      // Update the bookings state with the new status
      if (response  && response.status === newStatus) {
        setBookings(prevBookings =>
          prevBookings.map(b =>
            b.bookingId === booking.bookingId
              ? { ...b, status: newStatus }
              : b
          )
        );
      } else {
        console.warn('Booking status not updated or mismatch:', response);
        setError(t('admin.table_management.errors.update_failed'));
      }
    } catch (error) {
      console.error('Error updating booking status:', error);
      setError(t('admin.table_management.errors.booking_status_update_failed', { message: error.message }));
    }
  };

      if (loading) {
    return (
      <div className="table-management">
        <div className="loading-container">
          <p>{t('admin.table_management.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="table-management">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={() => window.location.reload()}>{t('admin.table_management.retry')}</button>
        </div>
      </div>
    );
  }  
  return (
    <div className="table-management">
      <div className="table-management-header">
        <h1 className="table-management-title">{t('admin.table_management.title')}</h1>
        <p className="table-management-subtitle">
          {t('admin.table_management.subtitle')}
        </p>
      </div>

      <TableSummary tables={tables} bookings={bookings} />

      <div className="table-content">
          <TableMap
            tables={tables}
            bookings={bookings}
            onUpdateTableStatus={handleUpdateTableStatus}
            onUpdateBookingStatus={handleUpdateBookingStatus}
          />
      </div>
    </div>
  );
}